import React, { useState } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import ColumnFilterContent from './ColumnFilter';
import { cn } from '@/lib/utils';
import type { QueryListFilter } from '@kirin/api-client/contracts/extensions';

export const FilterCondition = {
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
} as const;

export type FilterConditionType =
  (typeof FilterCondition)[keyof typeof FilterCondition];

/**
 * @deprecated Use QueryListFilter from @kirin/api-client/contracts/extensions instead
 */
export type Filter = QueryListFilter;

export interface ColumnFilterProps {
  /** The field name to filter on (e.g., 'source', 'publisher') */
  field: string;
  /** Display name for the filter (e.g., 'Source', 'Publisher') */
  fieldName: string;
  options: string[];
  onFilterChange: (filter: QueryListFilter) => void;
  onCancel?: () => void;
  className?: string;
  renderOption?: (option: string, isSelected: boolean) => React.ReactNode;
  currentFilter?: QueryListFilter;
  /** Whether to include an empty/None option at the top */
  includeEmptyOption?: boolean;
  /** Label for the empty option (defaults to "None") */
  emptyOptionLabel?: string;
}

/**
 * ColumnFilter is a wrapper component that renders the ColumnFilterContent
 * inside a popover with a filter icon trigger.
 */
export const ColumnFilter: React.FC<ColumnFilterProps> = ({
  field,
  fieldName,
  options,
  onFilterChange,
  onCancel,
  className,
  renderOption,
  currentFilter,
  includeEmptyOption = false,
  emptyOptionLabel = 'None',
}) => {
  const [open, setOpen] = useState(false);

  // Add empty option at the top if requested
  const optionsWithEmpty = includeEmptyOption ? ['', ...options] : options;

  const handleApply = (values: string[], condition: 'include' | 'exclude') => {
    onFilterChange({
      field,
      condition,
      values,
    });
    setOpen(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setOpen(false);
  };

  // Check if filter is active (has values)
  const isFilterActive = currentFilter && currentFilter.values.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn('relative', className)}
          title={`Filter by ${fieldName}`}
        >
          <ListFilter
            className={cn(
              'size-4',
              isFilterActive ? 'text-primary' : 'text-muted-foreground'
            )}
            strokeWidth={isFilterActive ? 2.75 : 2}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <ColumnFilterContent
          fieldName={fieldName}
          options={optionsWithEmpty}
          onApply={handleApply}
          onCancel={handleCancel}
          renderOption={renderOption}
          currentFilter={currentFilter}
          emptyOptionLabel={emptyOptionLabel}
        />
      </PopoverContent>
    </Popover>
  );
};
